import React, { useContext, useEffect, useMemo, useState } from 'react';
import Contact, { CreateContactOptions } from 'src/interfaces/api/Contact';
import useContact from 'src/hooks/api/useContact';
import Site from 'src/interfaces/api/Site';

interface ContextValue {
  contacts: Contact[];
  siteContacts: Contact[];
  create: (contact: CreateContactOptions) => void;
  get: (contact: Contact['id']) => void;
  loading: boolean;
}

const Context = React.createContext<ContextValue | undefined>(undefined);

interface ProviderProps {
  siteId?: Site['id'];
}

const isCalling = new Map();

export const ContactsContextProvider: React.FC<ProviderProps> = (props) => {
  const { children, siteId } = props;
  const [siteContacts, setSiteContacts] = useState<Contact[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);

  const { getBySite: getSiteContacts, get, create } = useContact();

  useEffect(() => {
    let ignore = false;

    if (siteId) {
      setLoading(true);
      getSiteContacts(siteId)
        .then((contacts) => {
          setLoading(false);

          if (ignore) return;

          setSiteContacts(contacts);
        })
        .catch(() => {
          setLoading(false);
        });
    }

    return () => {
      ignore = true;
    };
  }, [siteId, fetch]);

  const contextValue = useMemo<ContextValue>(
    () => ({
      contacts,
      siteContacts,
      loading,
      get: (contactId: Contact['id']) => {
        let isThisAPICalling = isCalling.get(contactId);

        if (!isThisAPICalling) {
          isThisAPICalling = get(contactId);
          isCalling.set(contactId, isThisAPICalling);
        }

        isThisAPICalling.then((contact) => {
          isCalling.set(contactId, null);

          setContacts((c) => {
            if (-1 === c.findIndex((cc) => cc.id === contact.id)) {
              return [...c, contact];
            }
            return c;
          });
        });
      },
      create: siteId
        ? async (contact: CreateContactOptions) => {
            const newContact = await create(siteId, contact);
            setContacts((c) => [...c, newContact]);
          }
        : () => {
            throw new Error('Aucun identifiant de site trouvé');
          },
    }),
    [fetch, contacts, siteContacts, isCalling]
  );

  return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

export default function useContacts(): ContextValue {
  const context = useContext(Context);

  if (context === undefined) {
    throw new Error('You must wrap components that call `useContacts` by a `ContactsContextProvider`');
  }

  return context;
}
